import React from "react";
import { Link } from "react-router-dom";
import { Plane } from "lucide-react";

/**
 * Site Footer
 *
 * Standard-shape footer used on every route. Contains:
 *  - Brand line + tagline
 *  - Product / Legal / Support link columns
 *  - Compliance notices (subscription auto-renewal, arbitration, cookie consent hook)
 *  - Copyright + social
 */
export default function Footer() {
  // Hide footer when embedded (e.g. Digital ID iframe on Cockpit page) so the
  // iframe only shows the badge content, not the surrounding site chrome.
  if (typeof window !== "undefined" && window.location.search.includes("embed=1")) {
    return null;
  }
  const year = new Date().getFullYear();
  return (
    <footer className="mt-20 border-t border-white/[0.06] bg-black/60" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-full bg-cyan/10 border border-cyan/40 grid place-items-center">
                <Plane className="h-4 w-4 text-cyan" />
              </div>
              <div>
                <div className="font-display text-lg font-bold leading-none">Private Jet Pilots</div>
                <div className="mono text-[10px] text-cyan tracking-widest">AVIATION OPS NETWORK</div>
              </div>
            </div>
            <p className="text-xs text-white/50 leading-relaxed max-w-sm">
              The verified network for pilots, flight attendants, mechanics, brokers, and
              operators — connecting 26,500+ aviation professionals to trusted trips, parts,
              and community since 2018.
            </p>
          </div>

          <FooterCol title="Product">
            <FooterLink to="/dashboard">Dashboard</FooterLink>
            <FooterLink to="/jobs">Job Board</FooterLink>
            <FooterLink to="/marketplace">Marketplace</FooterLink>
            <FooterLink to="/aog">AOG Board</FooterLink>
            <FooterLink to="/aircraft-sales">Aircraft Sales</FooterLink>
            <FooterLink to="/fuel">PJP Fuel</FooterLink>
          </FooterCol>

          <FooterCol title="Legal">
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to="/subscription-terms">Subscription Terms</FooterLink>
            <FooterLink to="/refund-policy">Refund Policy</FooterLink>
            <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
          </FooterCol>

          <FooterCol title="Support">
            <FooterLink to="/contact">Contact us</FooterLink>
            <FooterLink to="/membership">Membership</FooterLink>
            <FooterLink to="/settings/account">Account settings</FooterLink>
            <a href="mailto:privacy@privatejetpilots.com" className="footer-link">privacy@privatejetpilots.com</a>
          </FooterCol>
        </div>

        {/* Compliance notices */}
        <div className="mt-10 pt-6 border-t border-white/[0.05] grid md:grid-cols-2 gap-6 text-[10px] leading-relaxed text-white/40 mono">
          <div>
            <span className="text-white/70">AUTO-RENEWAL NOTICE.</span> PJP memberships renew
            automatically each year on the anniversary of your enrollment at the then-current
            rate until cancelled. You may cancel anytime from{" "}
            <Link to="/settings/account" className="text-cyan hover:underline">Account Settings</Link>
            {" "}— cancellation takes effect at the end of the current billing period.
          </div>
          <div>
            <span className="text-white/70">DO NOT SELL MY INFO.</span> PJP never sells personal
            data. California and Virginia residents may exercise CCPA / VCDPA rights via{" "}
            <a href="mailto:privacy@privatejetpilots.com" className="text-cyan hover:underline">privacy@privatejetpilots.com</a>.
            See our <Link to="/privacy" className="text-cyan hover:underline">Privacy Policy</Link>.
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="mono text-[10px] text-white/40">
            © {year} Private Jet Pilots, LLC. All rights reserved.
          </div>
          <div className="mono text-[10px] text-white/40">
            Made in the USA · v1.0
          </div>
        </div>
      </div>
      <style>{`
        .footer-link { display:block; color:rgba(255,255,255,.55); font-size:12px; padding:2px 0; transition:color .15s; }
        .footer-link:hover { color:#3AB0FF; }
      `}</style>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="overline text-white/45 mb-3">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function FooterLink({ to, children }) {
  return <Link to={to} className="footer-link">{children}</Link>;
}
